import './App.css';
import React from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import locations from './data/locations.json';

const containerStyle = {
    width: "100%",
    height: "90vh",
};

const center = {
    lat: 46.641437,
    lng: 24.0044832,
};

function haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

function App() {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: "AIzaSyB8kGWLJIur-7P9bp-HlTim5n3bHssyNXM",
    });

    const [selectedLocations, setSelectedLocations] = React.useState([]);
    const [clickedPoint, setClickedPoint] = React.useState(null);
    const [estimate, setEstimate] = React.useState(null);

    const handleMapClick = (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setClickedPoint({ lat, lng });

        // Calculează media ponderată
        let weightSum = 0;
        let nitritiSum = 0;
        let nitratiSum = 0;
        const p = 2; // exponent

        for (let loc of locations) {
            const dist = haversineDistance(lat, lng, loc.lat, loc.lng);
            // Pentru a evita divizare la 0, adăugăm o distanță minimă
            const adjDist = Math.max(dist, 0.001);
            const weight = 1 / Math.pow(adjDist, p);

            nitritiSum += loc.nitriti * weight;
            nitratiSum += loc.nitrati * weight;
            weightSum += weight;
        }

        const estNitriti = (nitritiSum / weightSum).toFixed(2);
        const estNitrati = (nitratiSum / weightSum).toFixed(2);

        setEstimate({
            nitriti: estNitriti,
            nitrati: estNitrati,
        });
    };

    if (!isLoaded) return <div>Loading...</div>;

    return (
        <div>
            <h1 style={{ textAlign: "center" }}>Calitatea apei în fântânile din Hodăi-Boian</h1>
            <h3 style={{ textAlign: "center" }}>Analize reale ale concentrațiilor de nitriți, nitrați și amoniu în zonele marcate și estimări (medie ponderată) pentru nitriți și nitrați în zonele nemarcate</h3>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={15}
                mapTypeId="hybrid"
                onClick={handleMapClick}
            >
                {locations.map((location) => (
                    <Marker
                        key={location.id}
                        position={{ lat: location.lat, lng: location.lng }}
                        onClick={() => {
                            setSelectedLocations(prev => {
                                if (prev.find(loc => loc.id === location.id)) return prev;
                                const updated = [...prev, location];
                                return updated.length > 3 ? updated.slice(1) : updated;
                            });
                        }}
                        icon={{
                            url: 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#0066cc"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4" fill="white"/></svg>'),
                            scaledSize: new window.google.maps.Size(36, 36)
                        }}
                    />
                ))}

                {selectedLocations.map(location => (
                    <InfoWindow
                        key={location.id}
                        position={{ lat: location.lat, lng: location.lng }}
                        onCloseClick={() => setSelectedLocations(prev => prev.filter(loc => loc.id !== location.id))}
                    >
                        <div style={{
                            padding: '16px',
                            minWidth: '200px',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h3 style={{
                                    margin: '0',
                                    fontSize: '16px',
                                    color: '#1a1a1a',
                                    fontWeight: '600'
                                }}>{location.address}</h3>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#666', fontSize: '14px' }}>Nitriți</span>
                                    <span style={{ fontWeight: '500', color: '#0066cc' }}>{location.nitriti} mg/L</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#666', fontSize: '14px' }}>Nitrați</span>
                                    <span style={{ fontWeight: '500', color: '#0066cc' }}>{location.nitrati} mg/L</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#666', fontSize: '14px' }}>Amoniu</span>
                                    <span style={{ fontWeight: '500', color: '#0066cc' }}>{location.amoniu} mg/L</span>
                                </div>
                            </div>
                        </div>
                    </InfoWindow>
                ))}

                {clickedPoint && (
                    <Marker
                        position={clickedPoint}
                        icon={{
                            url: 'data:image/svg+xml;base64,' + btoa('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="#ff4444"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4" fill="white"/></svg>'),
                            scaledSize: new window.google.maps.Size(32, 32)
                        }}
                    />
                )}

                {clickedPoint && estimate && (
                    <InfoWindow position={clickedPoint} onCloseClick={() => setClickedPoint(null)}>
                        <div style={{
                            padding: '16px',
                            minWidth: '220px',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h4 style={{
                                    margin: '0',
                                    fontSize: '16px',
                                    color: '#1a1a1a',
                                    fontWeight: '600'
                                }}>Estimare la punctul selectat</h4>
                                <button
                                    onClick={() => setClickedPoint(null)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '18px',
                                        cursor: 'pointer',
                                        color: '#666',
                                        padding: '0',
                                        lineHeight: '1'
                                    }}
                                >×</button>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#666', fontSize: '14px' }}>Nitriți estimați</span>
                                    <span style={{ fontWeight: '500', color: '#ff4444' }}>{estimate.nitriti} mg/L</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#666', fontSize: '14px' }}>Nitrați estimați</span>
                                    <span style={{ fontWeight: '500', color: '#ff4444' }}>{estimate.nitrati} mg/L</span>
                                </div>
                            </div>
                        </div>
                    </InfoWindow>
                )}
            </GoogleMap>
        </div>
    );
}

export default App;
